import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Well, Drawer, HighlightedTextArea } from '@pushly/aqe/lib/components'
import { EditOutlined, MessageOutlined } from '@ant-design/icons'
import { Flex, Skeleton, Splitter, Typography } from 'antd'
import { useService } from '@pushly/aqe/lib/hooks'
import * as randomstring from 'randomstring'
import { AppService, AiGenerationDirectiveService } from '../../services'
import { getClassNames } from '../../_utils/classnames'
import { simpleNotification } from '../../_utils/utils'
import { AiGenerationDirective } from '@pushly/models/lib/structs/pushly-ai/ai-generation-directive'
import { CompiledDirective } from '@pushly/models/lib/structs/pushly-ai/compiled-directive'
import { CreateAiGenerationDirectiveRequest } from '@pushly/models/lib/structs/pushly-ai/create-ai-generation-directive-request'
import { UpdateAiGenerationDirectiveRequest } from '@pushly/models/lib/structs/pushly-ai/update-ai-generation-directive-request'
import { BrandVoiceDecomposition, BrandVoiceSummaryChips, InfoRow, CompileState } from './brand-voice-decomposition'
import { findVerbatimSpans, HIGHLIGHT_TIER_STYLES } from './text-highlight'

const PLACEHOLDER =
    'e.g. Write like a trusted editor. Never use exclamation points. Keep titles under 60 characters. Favor curiosity over hype.'

interface IBrandVoiceEditor {
    domainId: number
    directive: AiGenerationDirective | null
    visible: boolean
    onClose: (e: any) => void
    onSaved: (directive: AiGenerationDirective) => void
}

const BrandVoiceEditor: FC<IBrandVoiceEditor> = (props) => {
    const appSvc = useService(AppService)
    const directiveSvc = useService(AiGenerationDirectiveService)

    // Textareas normalize \r\n/\r to \n internally for both their rendered value and
    // selectionStart/layout metrics — but the raw server body can still contain \r\n. If `body`
    // state kept those \r characters, every span offset computed against it would drift right
    // by one character per line break that precedes the match (the browser already dropped
    // that \r when laying the text out), compounding across paragraphs. Normalizing at the one
    // point raw server text enters state keeps our offsets and the browser's in agreement.
    const initialBody = (props.directive?.active_version?.body ?? '').replace(/\r\n?/g, '\n')
    const initialCompiled = props.directive?.active_version?.compiled_rules ?? null

    const [body, setBody] = useState<string>(initialBody)
    const [isDirty, setIsDirty] = useState(false)
    const [saving, setSaving] = useState(false)
    const [compileState, setCompileState] = useState<CompileState>(initialCompiled ? 'done' : 'idle')
    const [compiled, setCompiled] = useState<CompiledDirective | null>(initialCompiled)
    // The body whatever `compiled` currently holds actually reflects (a persisted result on
    // load, or the last successful recompile) — lets the effect below tell "the author edited
    // the text" apart from "the component re-rendered with the same text", so opening the
    // drawer on a directive with a saved decomposition never fires a redundant compile call.
    const lastCompiledBodyRef = useRef<string | undefined>(initialCompiled ? initialBody : undefined)

    // Textarea <-> rule-row highlight linking (NS-2668). Spans come straight off `compiled`'s
    // own attribution (each item's `sources`, resolved server-side against the exact body that
    // produced it — see text-highlight.ts) rather than a client-side re-search, so they only
    // change when a new `compiled` result actually lands, not on every keystroke. Between an
    // edit and the next Analyze click landing, spans stay pinned to the last-compiled body's
    // positions rather than the text currently on screen — expected, since nothing downstream
    // (the rule rows) has changed either until that compile completes.
    const [activeSpanKey, setActiveSpanKey] = useState<string | null>(null)
    const spans = useMemo(() => (compiled ? findVerbatimSpans(compiled) : []), [compiled])
    const matchedSpanKeys = useMemo(() => new Set(spans.map((s) => s.key)), [spans])

    // A click (not hover) also requests a scroll — `nonce` forces the request through even
    // when clicking the SAME row twice in a row (the textarea may have since been scrolled
    // away by the author's own typing/scrolling), which a bare key alone wouldn't re-trigger
    // since it wouldn't be seen as a change by HighlightedTextArea's effect dependency.
    const scrollNonceRef = useRef(0)
    const [scrollToSpan, setScrollToSpan] = useState<{ key: string; nonce: number } | null>(null)
    const handleSpanClick = (key: string) => {
        setActiveSpanKey(key)
        setScrollToSpan({ key, nonce: ++scrollNonceRef.current })
    }

    // The most recently ISSUED compile request's text — not the most recently RESOLVED one.
    // Debouncing only cancels a pending timer; it can't cancel a request already in flight, so
    // two compiles can legitimately overlap (e.g. the author keeps editing while a slow compile
    // of a long body is still running). Without this guard, whichever response happens to
    // resolve last wins regardless of which was issued last — a stale, shorter-body response
    // arriving after a newer one would silently overwrite it with rule text and highlight
    // offsets that no longer match what's on screen.
    const latestRequestedTextRef = useRef<string>('')

    const runCompile = useCallback(
        async (text: string) => {
            latestRequestedTextRef.current = text

            if (!text.trim()) {
                setCompiled(null)
                setCompileState('idle')
                lastCompiledBodyRef.current = undefined
                return
            }
            setCompileState('compiling')
            const res = await directiveSvc.compilePreview(props.domainId, text)

            // A newer request has since been issued (and will supersede this one on its own
            // resolution) — applying this now-stale result would only reintroduce the race.
            if (latestRequestedTextRef.current !== text) return

            if (res.ok && res.data) {
                setCompiled(res.data)
                setCompileState('done')
                lastCompiledBodyRef.current = text
            } else {
                setCompileState('error')
            }
        },
        [directiveSvc, props.domainId],
    )

    // Explicit "Analyze" trigger (NS-2668), not a debounced auto-compile: firing a compile
    // after every pause in typing meant any hesitation mid-sentence kicked off a request
    // against still-incomplete text — annoying in itself, and the source of most of the
    // "stale result overwritten by a slower one" races `latestRequestedTextRef` above exists
    // to guard against. The author decides when the text is actually ready to analyze.
    // `isStale` is what the Analyze button and decomposition below key off of to signal that
    // the rules on screen no longer reflect what's currently written.
    // Compared against `body.trim()`, not raw `body`: `runCompile` is always called with
    // trimmed text below (see its call sites), matching exactly what `handleSubmit` persists
    // as the directive's body. Comparing untrimmed `body` here would flag surrounding
    // whitespace alone as "changed since last analyzed", and — more importantly — letting
    // Analyze and Submit ever compile two DIFFERENT strings for the same author-visible text
    // is exactly what broke `compileAndPersist`'s equality check server-side: its `sources`
    // spans are absolute character offsets into whatever exact string was compiled, so even a
    // stray trailing newline shifts every offset and makes an otherwise-identical recompile
    // fail to match.
    const isStale = body.trim() !== '' && body.trim() !== lastCompiledBodyRef.current

    const handleSubmit = async () => {
        const trimmed = body.trim()
        if (!trimmed) {
            simpleNotification('error', 'Brand Voice cannot be empty')
            return
        }
        if (!compiled) {
            // `disableSubmit` below should prevent reaching here without a completed, current
            // compile — defensive guard, not the primary gate.
            simpleNotification('error', 'Please wait for the Brand Voice to finish analyzing before saving')
            return
        }

        setSaving(true)
        try {
            const res = props.directive
                ? await directiveSvc.update(
                      props.domainId,
                      props.directive.id,
                      UpdateAiGenerationDirectiveRequest.from({ body: trimmed }),
                  )
                : await directiveSvc.create(
                      props.domainId,
                      CreateAiGenerationDirectiveRequest.from({
                          role: 'brand_voice',
                          name: 'Brand Voice',
                          body: trimmed,
                      }),
                  )

            if (!res.ok || !res.data) return

            // Persist the EXACT decomposition just displayed and approved (`compiled`) — the
            // server independently recompiles and only writes it if its own fresh result
            // matches, so the next time this directive opens, its saved rules are there to
            // show immediately. Reuses the Redis pre-warm from the Analyze click above (same
            // exact text, since Submit is disabled until that Analyze result is current), so
            // this is typically instant.
            const compiledRes = await directiveSvc.compileAndPersist(props.domainId, res.data.id, compiled)

            if (compiledRes.ok && compiledRes.data) {
                simpleNotification('success', 'Brand Voice was successfully updated')
                props.onSaved(compiledRes.data)
            } else {
                // The server's fresh recompile no longer matched what was reviewed (an expired
                // preview cache, a compiler change, or a stale review) — the text itself saved,
                // but the reviewed decomposition was NOT persisted rather than silently
                // persisting something different from what was approved.
                simpleNotification(
                    'warning',
                    "Brand Voice text was saved, but its rules changed since you last reviewed them. Reopen to re-check before they're enforced.",
                )
                props.onSaved(res.data)
            }
        } finally {
            setSaving(false)
        }
    }

    return (
        <Drawer
            getContainer={appSvc.getAppContainer}
            className={getClassNames('ddf-brand-voice-editor')}
            title="Update Brand Voice"
            placement="right"
            closable={true}
            onSubmit={handleSubmit}
            // Never enabled mid-compile or against stale/errored text — Submit only becomes
            // available once `compiled` reflects a successfully-finished compile of the exact
            // text on screen, so what gets persisted is guaranteed to be what was reviewed.
            //
            // Deliberately NOT gated on `saving`: aqe's SubmitButton renders a structurally
            // different element (a bare span vs. a Tooltip-wrapped one) depending on whether
            // it's disabled, so flipping `disableSubmit` true as a synchronous side effect of
            // the click (which `setSaving(true)` inside handleSubmit is) remounts the inner
            // Button mid-click and discards the loading state it had just set — the spinner
            // never gets a chance to paint. Re-entrancy during the save is already handled by
            // aqe's own Button (it no-ops a click while its internal `isLoading` is true), so
            // this doesn't need its own guard against a double-submit.
            disableSubmit={!isDirty || compileState !== 'done' || body.trim() !== lastCompiledBodyRef.current}
            onClose={props.onClose}
            open={props.visible}
            size={1280}
        >
            <Flex vertical gap={16} className={getClassNames('brand-voice-editor-body')}>
                <Typography.Text type="secondary">
                    Free-text guidance, split into rules we can guarantee and style we apply best-effort.
                </Typography.Text>

                {/* A direct viewport-relative height, not a percentage chained up through
                    Flex -> the antd Drawer body -> the Drawer's own panel sizing — that chain
                    depends on several nested elements each correctly resolving a definite
                    height for a `height: 100%` below them to mean anything, and in practice it
                    collapsed to the textarea's tiny intrinsic default instead of filling the
                    drawer. A fixed calc against the viewport sidesteps all of that: 240px
                    accounts for the drawer header, its body padding, and the intro paragraph
                    above this row: same on-screen effect, without depending on ancestors we
                    don't control resolving heights the way we'd expect. `Splitter.Panel`
                    children stretch to fill this explicit height automatically (antd's own
                    cross-axis stretch, not something we have to wire up). */}
                <Splitter style={{ height: 'calc(100vh - 240px)', minHeight: 320 }}>
                    <Splitter.Panel style={{ minWidth: '33%' }}>
                        <div
                            style={{
                                margin: '0 12px',
                                height: '100%',
                                minHeight: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 6,
                            }}
                        >
                            <Flex align="center" justify="space-between">
                                <Typography.Text strong>Your Brand Voice</Typography.Text>
                                <Button
                                    size="small"
                                    type={isStale ? 'primary' : 'default'}
                                    loading={compileState === 'compiling'}
                                    // Also blocked while saving — a click here mid-save would
                                    // kick off a recompile against text the save is actively
                                    // persisting, whose result could land after the save
                                    // finishes and make `compiled` disagree with what was just
                                    // written.
                                    disabled={compileState === 'compiling' || !isStale || saving}
                                    // Trimmed to match exactly what handleSubmit persists as
                                    // the directive's body — see `isStale`'s docstring above.
                                    onClick={() => runCompile(body.trim())}
                                >
                                    Analyze
                                </Button>
                            </Flex>
                            <HighlightedTextArea
                                value={body}
                                onChange={(value) => {
                                    // Defensive: the DOM's textarea sanitization already normalizes
                                    // typed/pasted input to \n, but keep state consistent regardless
                                    // of how a future caller might set this value.
                                    setBody(value.replace(/\r\n?/g, '\n'))
                                    setIsDirty(true)
                                }}
                                placeholder={PLACEHOLDER}
                                spans={spans}
                                activeSpanKey={activeSpanKey}
                                onSpanHover={setActiveSpanKey}
                                scrollToSpan={scrollToSpan}
                                tierStyles={HIGHLIGHT_TIER_STYLES}
                            />
                            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                {isStale
                                    ? 'The rules on the right are from an earlier version of this text — click Analyze to refresh them.'
                                    : 'Click Analyze to see how your text breaks down. Checked/Needs review rules highlight the text they came from, and vice versa — click a rule to jump to it.'}
                            </Typography.Text>
                        </div>
                    </Splitter.Panel>
                    <Splitter.Panel style={{ flex: 2, minWidth: '45%' }}>
                        <div
                            style={{
                                margin: '0 12px',
                                paddingTop: 28,
                                height: '100%',
                                minHeight: 0,
                                overflowY: 'auto',
                            }}
                        >
                            <BrandVoiceDecomposition
                                state={compileState}
                                compiled={compiled}
                                onRetry={() => runCompile(body.trim())}
                                activeSpanKey={activeSpanKey}
                                matchedSpanKeys={matchedSpanKeys}
                                onSpanHover={setActiveSpanKey}
                                onSpanClick={handleSpanClick}
                            />
                        </div>
                    </Splitter.Panel>
                </Splitter>
            </Flex>
        </Drawer>
    )
}

interface IBrandVoiceWell {
    domainId: number
    canEdit: boolean
}

export const BrandVoiceWell: FC<IBrandVoiceWell> = (props) => {
    const directiveSvc = useService(AiGenerationDirectiveService)

    const [loading, setLoading] = useState(true)
    const [directive, setDirective] = useState<AiGenerationDirective | null>(null)
    const [showDrawer, setShowDrawer] = useState(false)
    const [drawerKey, setDrawerKey] = useState<string>('')

    const loadBrandVoice = async () => {
        setLoading(true)
        const res = await directiveSvc.fetchBrandVoice(props.domainId)
        if (res.ok) {
            setDirective(res.data ?? null)
        }
        setLoading(false)
    }

    useEffect(() => {
        loadBrandVoice()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.domainId])

    const body = directive?.active_version?.body
    const compiledRules = directive?.active_version?.compiled_rules ?? null

    return (
        <Well
            className="nested"
            title="Brand Voice"
            actions={
                <Button
                    size="small"
                    onClick={() => {
                        setDrawerKey(randomstring.generate())
                        setShowDrawer(true)
                    }}
                    disabled={!props.canEdit || loading}
                >
                    <EditOutlined />
                    <span>Edit</span>
                </Button>
            }
            footerMode="hidden"
        >
            <Skeleton loading={loading} active={true} title={false}>
                {!loading &&
                    (body ? (
                        <Flex vertical gap={10}>
                            {/* Leads with what's actually structured/enforced — the free-text
                                body explains what was SAID, not what got ENFORCED, so it's
                                demoted to supporting detail once we have something to
                                summarize. Falls back to text-only for a directive saved before
                                compiled_rules was reliably persisted (or mid-first-compile). */}
                            {compiledRules && <BrandVoiceSummaryChips compiled={compiledRules} />}
                            {compiledRules ? (
                                // Demoted below the chips by size and a neutral card, not by
                                // washing the color out (antd's `type="secondary"` reads too
                                // faint for a full paragraph) — still fully legible, but reads
                                // as a quoted excerpt of what was typed (matching the app's own
                                // neutral "summary card" pattern, e.g. Vespa's recommendation
                                // config "Configuration Summary" box) rather than as this
                                // card's primary content or app-authored copy.
                                <div style={{ maxWidth: 640 }}>
                                    <InfoRow
                                        icon={<MessageOutlined />}
                                        style={{ backgroundColor: 'var(--ant-color-fill-quaternary)' }}
                                    >
                                        <Typography.Paragraph
                                            style={{ whiteSpace: 'pre-wrap', marginBottom: 0, fontSize: 13 }}
                                        >
                                            {body}
                                        </Typography.Paragraph>
                                    </InfoRow>
                                </div>
                            ) : (
                                <Typography.Paragraph style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>
                                    {body}
                                </Typography.Paragraph>
                            )}
                        </Flex>
                    ) : (
                        <Typography.Text type="secondary">
                            No Brand Voice set. AI-generated content will use default behavior.
                        </Typography.Text>
                    ))}
            </Skeleton>

            <BrandVoiceEditor
                key={drawerKey}
                domainId={props.domainId}
                directive={directive}
                visible={showDrawer}
                onClose={() => setShowDrawer(false)}
                onSaved={(updated) => {
                    setDirective(updated)
                    setShowDrawer(false)
                }}
            />
        </Well>
    )
}
